import React, { useEffect, useState } from "react";
import StepLabel from "@material-ui/core/StepLabel";
import { validateCryptoWallet } from "../../../utils/attributesValidator";
import DefaultButton from "../../styles/materialButtonStyle";
import InvoiceFirstStep from "./InvoiceSteps/InvoiceFirstStep";
import InvoiceSecondStep from "./InvoiceSteps/InvoiceSecondStep";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { fetchInvoiceAttributes } from "../../../utils/fetchInvoiceAttributes";
import Notification from "../notification/Notification";
import userAuthenticationConfig from "../../../utils/userAuthenticationConfig";
import { check2FA } from "../../../utils/check2FA";
import getUserInfo from "../../../utils/getUserInfo";
import {
  ATTRIBUTES, BOOLEAN_FLAGS,
  HTTP_STATUSES,
  MS_CASH,
  NULLABLE_ATTRIBUTES,
  PAYMENT_SYSTEM, PAYMENT_TYPE,
  STANDARD_ERRORS_WAY, TRANSACTION_TYPE_PAYMENT
} from "../../../utils/consts";
import { useTranslation } from "react-i18next";

import { StyledInvoiceWrapper } from "./styledInvoice";
import {
  StyledDialogAction,
  StyledDialogContent,
  StyledDialogHead,
  StyledDialogWrapper
} from "../../styles/styledDialog";
import MaterialDialog from "../../styles/materialDialog";
import { MaterialStep, MaterialStepper } from "../../styles/materialStepper";
import { parseAxiosError } from "../../../utils/response";
import { fetchFilterData } from "../../../utils/fetchFilterData";

const InvoiceDialog = ({ direction, fetchTransactionList }) => {
  const { t } = useTranslation("paymentsPage");
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [balanceInfo, setBalanceInfo] = useState(null);

  const [notification, setNotification] = useState({
    visible: false,
    type: "",
    message: ""
  });

  const mandatoryDataForNextStep = {
    "service": null,
    "project": null,
    "paymentSystem": null,
    "currency": null,
    "amount": null,
    "note": null
  };

  const defaultData = Object.assign({
    "attributes": null,
    "connection": null,
    "returnUrl": process.env.REACT_APP_DEFAULT_URL_FOR_INVOICE,
    "callBackUrl": process.env.REACT_APP_DEFAULT_URL_FOR_INVOICE,
    "transactionType": TRANSACTION_TYPE_PAYMENT.ORDER,
    "relatedReferenceId": "",
    "twofa": null
  }, mandatoryDataForNextStep);

  const [invoiceData, setInvoiceData] = useState(defaultData);

  useEffect(() => {
    setDisabled(false);
  }, [notification]);

  const validateAttributes = (userAttribute, attributes) => {
    let checkFields = false;
    const attribute = attributes.find(attribute => attribute.id === userAttribute.attribute);
    const checkValue = userAttribute.value === undefined ? "" : userAttribute.value;

    let regexp = new RegExp(attribute.regex.replace(/^.|.$/g, ""));

    switch (attribute.name) {
      case ATTRIBUTES.WALLET: {
        if (!validateCryptoWallet(invoiceData.currency.asset, checkValue, invoiceData.paymentSystem.subName)) {
          setErrors((prevState) => ({
            ...prevState,
            [attribute.name]: `Invalid wallet, example - ${attribute.placeholder}`
          }));
          checkFields = true;
        }
      }
        break;
      case ATTRIBUTES.STABLING_CURRENCY: {
        let isStabling = attributes.find(attr => attr.name === ATTRIBUTES.IS_STABLING);

        if (isStabling.value === BOOLEAN_FLAGS.FALSE) {
          break;
        }

        if (checkValue === null || checkValue === "") {
          checkFields = true;
        }
      }
        break;
      default: {
        if (!regexp.test(checkValue)) {
          setErrors((prevState) => ({
            ...prevState,
            [attribute.name]: `Invalid ${attribute.title}, example - ${attribute.placeholder}`
          }));
          checkFields = true;
        }
      }
    }

    return checkFields;
  };

  const getAvailableBalance = () => {
    let filterUrl = fetchFilterData({
      ["currency"]: invoiceData.currency.asset,
      ["service"]: invoiceData.service.name,
      ["connection"]: invoiceData.connection.id
    });

    axios.get("/api/balances/converted" + filterUrl, userAuthenticationConfig()).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_OK && Object.keys(response.data).length) {
        setBalanceInfo(response.data);
      }
    }).catch(error => {
      setNotification({ ...notification, visible: true, type: "error", message: error.response.data.message });
    });
  };

  const createInvoice = async (event) => {
    event.preventDefault();
    setDisabled(true);

    let checkFields = false;

    for (let [key, value] of Object.entries(defaultData)) {
      if ((invoiceData[key] === null || invoiceData[key] === "") && key !== "note" && key !== "relatedReferenceId") {
        checkFields = true;
        break;
      }
    }

    let attributes = await fetchInvoiceAttributes(invoiceData.attributes);

    for (let [key, value] of Object.entries(attributes)) {

      if ((value.value === null || value.value === "") &&
        !NULLABLE_ATTRIBUTES.includes(invoiceData.attributes.find((element) => element.id === value.attribute)?.name)) {

        checkFields = true;
        break;
      }
    }

    for (let i = 0; i < attributes.length; i++) {
      const validationRes = validateAttributes(attributes[i], invoiceData.attributes);

      if (!checkFields) {
        checkFields = validationRes;
      }
    }

    if (checkFields) {
      setNotification({
        ...notification,
        visible: true,
        type: "error",
        message: t("create.errors.checkFieldsMessage")
      });
      return;
    }

    if (invoiceData.amount <= 0) {
      setNotification({
        ...notification,
        visible: true,
        type: "error",
        message: t("create.errors.invoiceDataMessage")
      });
      return;
    }

    let check2Fa = await check2FA(getUserInfo()["userId"], invoiceData.twofa);

    if (check2Fa.error) {
      setNotification({ ...notification, visible: true, type: "error", message: check2Fa.error });
      return;
    }

    let data = {
      "paymentSystem": invoiceData.paymentSystem.subName,
      "amount": invoiceData.amount.replace(",", "."),
      "currency": invoiceData.currency.asset,
      "referenceId": uuidv4(),
      "attributes": attributes,
      "returnUrl": invoiceData.returnUrl,
      "callBackUrl": invoiceData.callBackUrl,
      "connection": invoiceData.connection.id,
      "paymentType": PAYMENT_TYPE.manual,
      "relatedReferenceId": invoiceData.relatedReferenceId,
      "transactionType": invoiceData.transactionType
    };

    if (data.paymentSystem === PAYMENT_SYSTEM.BALANCE_REFILING && invoiceData.service.name !== MS_CASH) {
      delete data.attributes;
    }

    // let hash = CryptoJS.HmacSHA256(JSON.stringify(data), invoiceData.projectInfo.projectSecretKey);
    //
    // data.signature = CryptoJS.enc.Base64.stringify(hash);
    data.note = invoiceData.note;

    axios.post("/api/" + direction, data, userAuthenticationConfig(false)).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_CREATED) {

        if (response.data.status.value === "FAIL") {
          setNotification({
            ...notification,
            visible: true,
            type: "error",
            message: JSON.stringify(response.data.flowData)
          });
          return;
        }

        if (invoiceData.paymentSystem.subName !== PAYMENT_SYSTEM.BALANCE_REFILING) {
          setNotification({
            ...notification,
            visible: true,
            type: "error",
            message: t("create.errors.frameMessage")
          });
        }

        fetchTransactionList();

        setInvoiceData(defaultData);
        setActiveStep(0);
        setOpen(false);

        setNotification({
          ...notification,
          visible: true,
          type: "success",
          message: t("create.notification.invoiceCreated")
        });
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    }).finally(() => setDisabled(false));
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const getSteps = () => {
    return [t("create.steps.one"), t("create.steps.two")];
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <InvoiceFirstStep
          direction={direction}
          invoiceData={invoiceData}
          setInvoiceData={setInvoiceData}
          balanceInfo={balanceInfo}
        />;
      case 1:
        return <InvoiceSecondStep
          direction={direction}
          invoiceData={invoiceData}
          setInvoiceData={setInvoiceData}
          setNotification={setNotification}
          notification={notification}
          errors={errors}
          setErrors={setErrors}
          handleClose={handleClose}
        />;
      default:
        return "Unknown step";
    }
  };

  const steps = getSteps();

  const handleNext = () => {
    let checkFields = false;

    for (let [key, value] of Object.entries(mandatoryDataForNextStep)) {
      if ((invoiceData[key] === null || invoiceData[key] === "") && key !== "note" && key !== "relatedReferenceId") {
        checkFields = true;
        break;
      }
    }

    if (checkFields) {
      setNotification({
        ...notification,
        visible: true,
        type: "error",
        message: t("create.errors.checkFieldsMessage")
      });
      return;
    }

    if (invoiceData.amount && !invoiceData.amount.match("^[+-]?\\d+(\\.\\d+)?$")) {
      setNotification({
        ...notification,
        visible: true,
        type: "error",
        message: t("create.errors.invoiceDataMessage")
      });
      return;
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleClose = () => {
    setInvoiceData(defaultData);
    setActiveStep(0);
    setOpen(false);
    setErrors({});
    setBalanceInfo(null);
  };

  useEffect(() => {
    if (
      !invoiceData.currency || !invoiceData.service ||
      !invoiceData.paymentSystem || !invoiceData.project || !invoiceData.connection
    ) {
      setBalanceInfo(null);

      return;
    }

    getAvailableBalance();
  }, [invoiceData.paymentSystem, invoiceData.currency, invoiceData.connection]);

  return (
    <>
      <Notification
        notification={notification}
        setNotification={setNotification}
      />
      <StyledInvoiceWrapper className={`${direction}-head__create-invoice`}>
        <DefaultButton
          color="success"
          onClick={handleClickOpen}
        >
          {t("create.create")}
        </DefaultButton>

        <MaterialDialog
          open={open}
        >
          <StyledDialogWrapper>
            <StyledDialogHead>
              {t("create.title")}
            </StyledDialogHead>
            <StyledDialogContent>
              <MaterialStepper activeStep={activeStep}>
                {steps.map((label) => {
                  const stepProps = {};
                  const labelProps = {};
                  return (
                    <MaterialStep key={label} {...stepProps}>
                      <StepLabel {...labelProps}>{label}</StepLabel>
                    </MaterialStep>
                  );
                })}
              </MaterialStepper>
              {getStepContent(activeStep)}
            </StyledDialogContent>
            <StyledDialogAction col="2">
              <DefaultButton
                onClick={handleClose}
                color="danger"
                disabled={disabled}
              >
                {t("create.cancel")}
              </DefaultButton>
              <DefaultButton
                color="success"
                onClick={activeStep === steps.length - 1 ? createInvoice : handleNext}
                disabled={disabled || Object.keys(errors).length > 0}
              >
                {activeStep === steps.length - 1 ? t("create.create") : t("create.next")}
              </DefaultButton>
            </StyledDialogAction>
          </StyledDialogWrapper>
        </MaterialDialog>
      </StyledInvoiceWrapper>
    </>
  );
};

export default InvoiceDialog;